/**
 * EditorEngine
 * ------------
 * A small, mark-based rich-text engine for a single `contenteditable` root.
 * It applies/removes inline marks (<strong>, <em>, <u>, and <span style="font-size">)
 * using Selection/Range APIs, while preserving the user's selection and
 * keeping the DOM tidy.
 */
export default class EditorEngine {
	/**
	 * @param {HTMLElement} rootEl - The contenteditable element the engine controls.
	 * @param {(html: string) => void} onChange - Called after each change with updated innerHTML.
	 */
	constructor(rootEl, onChange) {
		this.debug = true;

		// Basic guardrails
		if (!rootEl || rootEl.nodeType !== 1 || typeof rootEl.innerHTML !== 'string') {
			throw new Error('EditorEngine needs a root HTMLElement');
		}

		/** @type {HTMLElement} */
		this.root = rootEl;

		/** @type {(html: string) => void} */
		this.onChange = typeof onChange === 'function' ? onChange : () => {};

		/** @type {Range | null} */
		this._lastRange = null;

		// --- Selection bookkeeping (cache last non-collapsed selection inside the editor) ---
		this._cacheSelectionFromWindow = () => {
			const sel = window.getSelection();
			if (!sel || sel.rangeCount === 0) {
				this._lastRange = null;
				return;
			}

			const r = sel.getRangeAt(0);
			if (this.root.contains(r.commonAncestorContainer) && !r.collapsed) {
				this._lastRange = r.cloneRange();
			} else {
				this._lastRange = null;
			}
		};

		this._onSelectionChange = () => {
			this._cacheSelectionFromWindow();
			this._emitSelectionState();
		};
		// Document level: catches clicks/keyboard changes
		document.addEventListener('selectionchange', this._onSelectionChange);

		// Editor level: captures final selection after mouse drag / keyboard movement
		this.root.addEventListener('mouseup', this._cacheSelectionFromWindow);
		this.root.addEventListener('keyup', this._cacheSelectionFromWindow);

		this._clearCachedSelection = () => {
			this._lastRange = null;
		};

		document.addEventListener('mousedown', this._clearCachedSelection, true);

		this._cacheSelectionFromWindow = () => {
			const sel = window.getSelection();
			if (!sel || sel.rangeCount === 0) {
				this._lastRange = null;
				return;
			}
			const r = sel.getRangeAt(0);
			if (!this.root.contains(r.commonAncestorContainer)) {
				this._lastRange = null;
				return;
			}
			// Only cache non-collapsed selections
			if (!r.collapsed) this._lastRange = r.cloneRange();
			else this._lastRange = null;
		};
	}

	// =====================================================================
	// Lifecycle
	// =====================================================================

	/** Unbind listeners to avoid memory leaks. */
	destroy() {
		document.removeEventListener('selectionchange', this._onSelectionChange);
		this.root.removeEventListener('mouseup', this._cacheSelectionFromWindow);
		this.root.removeEventListener('keyup', this._cacheSelectionFromWindow);
		document.removeEventListener('mousedown', this._clearCachedSelection, true);
	}

	// =====================================================================
	// Public API
	// =====================================================================

	/** Replace editor contents with provided HTML. */
	setHtml(html) {
		this.root.innerHTML = html || '';
	}

	/** Get current innerHTML of the editor. */
	getHtml() {
		return this.root.innerHTML;
	}

	/** Toggle <strong> on current selection. */
	toggleBold() {
		this._toggleTag('strong');
	}

	/** Toggle <em> on current selection. */
	toggleItalic() {
		this._toggleTag('em');
	}

	/** Toggle <u> on current selection. */
	toggleUnderline() {
		this._toggleTag('u');
	}

	/**
	 * Set font size for current selection (wraps in <span style="font-size:…">).
	 * If selection is fully inside a span with a fontSize, updates it.
	 * @param {number} px - font-size in pixels
	 */
	setFontSize(px) {
		let activeRange = this._getActiveRangeOrNull();

		if (!activeRange) {
			this._restoreLastRange();
			activeRange = this._getActiveRangeOrNull();
			if (!activeRange) return;
		}

		const sharedSpan = this._commonWrapperForRange(activeRange, 'span', el => !!el.style && !!el.style.fontSize);

		if (sharedSpan) {
			sharedSpan.style.fontSize = `${px}px`;
		} else {
			this._wrapRangeWithTag(activeRange, 'span', el => {
				el.style.fontSize = `${px}px`;
			});
		}

		this._cleanInline();
		this._emit();
	}

	// =====================================================================
	// Selection utilities
	// =====================================================================

	/** Emit new HTML after a change. */
	_emit() {
		this.onChange(this.root.innerHTML);
	}

	/**
	 * Return current inline states for toolbar UI.
	 * - For collapsed caret: boolean presence of each mark at caret.
	 * - For non-collapsed: 'none' | 'mixed' | 'all' for each mark.
	 */
	getInlineState() {
		const sel = window.getSelection();
		if (!sel || sel.rangeCount === 0 || !this.root.contains(sel.anchorNode)) {
			return { strong: 'none', em: 'none', u: 'none' };
		}

		if (sel.isCollapsed) {
			const el = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
			const has = tag => !!(el && el.closest(tag) && this.root.contains(el.closest(tag)));
			return {
				strong: has('strong'),
				em: has('em'),
				u: has('u'),
			};
		} else {
			const r = sel.getRangeAt(0);
			return {
				strong: this._markStateInRange('strong', r),
				em: this._markStateInRange('em', r),
				u: this._markStateInRange('u', r),
			};
		}
	}

	/**
	 * Get the current active Range if it's inside the editor and non-collapsed.
	 * @returns {Range|null}
	 */
	_getActiveRangeOrNull() {
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) return null;

		const range = selection.getRangeAt(0);
		const isInsideEditor = this.root.contains(range.commonAncestorContainer);
		if (!isInsideEditor || range.collapsed) return null;

		return range;
	}

	/** Try to restore the last known in-editor selection range. */
	_restoreLastRange() {
		if (!this._lastRange) return false;
		const selection = window.getSelection();
		selection.removeAllRanges();
		selection.addRange(this._lastRange);
		return true;
	}

	/**
	 * Get current Range or select all content (used by commands that accept “no selection”).
	 * @returns {Range}
	 */
	_getRangeOrSelectAll() {
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) return this._selectAll();

		const range = selection.getRangeAt(0);
		const isInsideEditor = this.root.contains(range.commonAncestorContainer);
		if (!isInsideEditor || range.collapsed) return this._selectAll();

		return range;
	}

	/** Select the whole editor and return that Range. */
	_selectAll() {
		const range = document.createRange();
		range.selectNodeContents(this.root);
		const selection = window.getSelection();
		selection.removeAllRanges();
		selection.addRange(range);
		return range;
	}

	onSelectionStateChange(handler) {
		this._selectionListener = typeof handler === 'function' ? handler : null;
		// Emit once immediately so the toolbar initializes
		this._emitSelectionState();
	}

	_emitSelectionState() {
		if (!this._selectionListener) return;

		const sel = window.getSelection();
		const collapsed = !!(sel && sel.rangeCount && sel.isCollapsed && this.root.contains(sel.anchorNode));

		// For each mark, if collapsed we report 'inside' (boolean), else 'none/mixed/all'
		const computeMark = tag => {
			if (!sel || !sel.rangeCount) return 'none';
			const r = sel.getRangeAt(0);
			if (!this.root.contains(r.commonAncestorContainer)) return 'none';

			if (r.collapsed) {
				const el = r.startContainer.nodeType === 1 ? r.startContainer : r.startContainer.parentElement;
				const inside = !!(el && el.closest(tag) && this.root.contains(el.closest(tag)));
				return inside ? 'inside' : 'none';
			}
			// Non-collapsed: reuse your existing logic
			return this._markStateInRange(tag, r); // 'none' | 'mixed' | 'all'
		};

		const payload = {
			collapsed,
			strong: computeMark('strong'),
			em: computeMark('em'),
			u: computeMark('u'),
		};

		this._selectionListener?.(payload);
	}

	// =====================================================================
	// Core DOM editing primitives
	// =====================================================================

	// Treat aliases as the same mark
	_selectorForMark(tagName) {
		switch ((tagName || '').toLowerCase()) {
			case 'strong':
				return 'strong,b';
			case 'em':
				return 'em,i';
			case 'u':
				return 'u';
			case 'span':
				return 'span';
			default:
				return tagName;
		}
	}

	_matchesMark(el, tagName) {
		return !!(el && el.nodeType === 1 && el.matches(this._selectorForMark(tagName)));
	}

	_closestMark(node, tagName) {
		const el = node && (node.nodeType === 1 ? node : node.parentElement);
		return el ? el.closest(this._selectorForMark(tagName)) : null;
	}

	// Text that’s visually nothing (spaces, NBSP, ZWSP)
	_isWhitespaceText(node) {
		if (!node || node.nodeType !== Node.TEXT_NODE) return false;
		return node.nodeValue.replace(/[\u200B\u00A0\s]/g, '') === '';
	}

	// A wrapper that only contains nothing (or only ZWSP)
	_isEmptyMark(el) {
		if (!el || el.nodeType !== 1) return false;
		if (el.childNodes.length === 0) return true;
		if (el.childNodes.length === 1 && el.firstChild.nodeType === Node.TEXT_NODE) {
			return this._isWhitespaceText(el.firstChild);
		}
		// All children whitespace?
		for (const n of el.childNodes) {
			if (n.nodeType === 1) return false;
			if (!this._isWhitespaceText(n)) return false;
		}
		return true;
	}

	// Neighbor scanning that skips ignorable nodes
	_prevSignificantSibling(node) {
		let n = node && node.previousSibling;
		while (n && (this._isWhitespaceText(n) || (n.nodeType === 1 && this._isEmptyMark(n)))) {
			n = n.previousSibling;
		}
		return n;
	}
	_nextSignificantSibling(node) {
		let n = node && node.nextSibling;
		while (n && (this._isWhitespaceText(n) || (n.nodeType === 1 && this._isEmptyMark(n)))) {
			n = n.nextSibling;
		}
		return n;
	}

	_indexInParent(node) {
		const p = node?.parentNode;
		return p ? Array.prototype.indexOf.call(p.childNodes, node) : -1;
	}
	_placeCaretBefore(node) {
		if (!node || !node.parentNode) return;
		const doc = this.root.ownerDocument || document;
		const r = doc.createRange();
		const i = this._indexInParent(node);
		r.setStart(node.parentNode, Math.max(0, i));
		r.collapse(true);
		const sel = window.getSelection();
		sel.removeAllRanges();
		sel.addRange(r);
	}
	_placeCaretAfter(node) {
		if (!node || !node.parentNode) return;
		const doc = this.root.ownerDocument || document;
		const r = doc.createRange();
		const i = this._indexInParent(node);
		r.setStart(node.parentNode, i + 1);
		r.collapse(true);
		const sel = window.getSelection();
		sel.removeAllRanges();
		sel.addRange(r);
	}
	_removeIfEmptyAndPlaceOutside(el, placeAfter = true) {
		if (!el || el.nodeType !== 1) return false;
		const wasEmpty = this._isEmptyMark(el);
		const parent = el.parentNode;
		if (wasEmpty && parent) {
			const ref = el;
			el.remove();
			if (placeAfter) this._placeCaretAfter(ref);
			else this._placeCaretBefore(ref);
			return true;
		}
		return false;
	}

	isMarkActive(tagName) {
		const sel = window.getSelection();
		if (!sel || sel.rangeCount === 0) return false;
		const r = sel.getRangeAt(0);
		if (r.collapsed) {
			const el = r.startContainer.nodeType === 1 ? r.startContainer : r.startContainer.parentElement;
			return !!this._closestMark(el, tagName);
		}
		// for a range, report "all"
		return this._markStateInRange(tagName, r) === 'all';
	}

	_markSelector(tag) {
		// used for detection
		if (tag === 'strong' || tag === 'b') return 'strong,b';
		if (tag === 'em' || tag === 'i') return 'em,i';
		if (tag === 'u') return 'u';
		return tag;
	}
	_canonicalTag(tag) {
		// used when creating
		if (tag === 'b') return 'strong';
		if (tag === 'i') return 'em';
		return tag;
	}

	_canonicalizeInlineTags(root = this.root) {
		root.querySelectorAll('b').forEach(b => {
			const strong = document.createElement('strong');
			while (b.firstChild) strong.appendChild(b.firstChild);
			b.replaceWith(strong);
		});
		root.querySelectorAll('i').forEach(i => {
			const em = document.createElement('em');
			while (i.firstChild) em.appendChild(i.firstChild);
			i.replaceWith(em);
		});
	}

	/**
	 * Robust wrapper: add <tagName> around exactly the selection.
	 * Uses bookmark text nodes to avoid offset shifts while extracting.
	 * @param {Range} range
	 * @param {string} tagName
	 * @param {(el: HTMLElement) => void} [initEl] - optional initializer for the new element
	 */
	_wrapRangeWithTag(range, tagName, initEl) {
		const doc = this.root.ownerDocument || document;
		const wasCollapsed = range.collapsed;

		const newWrapper = doc.createElement(tagName);
		if (initEl) initEl(newWrapper);

		// Bookmarks: insert end first, then start (so offsets for start remain valid)
		const endBookmark = doc.createTextNode('');
		const endRange = range.cloneRange();
		endRange.collapse(false);
		endRange.insertNode(endBookmark);

		const startBookmark = doc.createTextNode('');
		const startRange = range.cloneRange();
		startRange.collapse(true);
		startRange.insertNode(startBookmark);

		// Extract exactly what's between bookmarks
		const between = doc.createRange();
		between.setStartAfter(startBookmark);
		between.setEndBefore(endBookmark);

		let extractedFragment;
		try {
			extractedFragment = between.extractContents(); // keeps live nodes / order
		} catch {
			// Last-resort fallback
			extractedFragment = between.cloneContents();
			between.deleteContents();
		}

		newWrapper.appendChild(extractedFragment);
		between.insertNode(newWrapper);

		// Cleanup bookmarks
		if (startBookmark.parentNode) startBookmark.parentNode.removeChild(startBookmark);
		if (endBookmark.parentNode) endBookmark.parentNode.removeChild(endBookmark);

		// Restore selection to the new wrapper
		const selection = window.getSelection();
		const newRange = doc.createRange();
		newRange.selectNodeContents(newWrapper);
		if (wasCollapsed) newRange.collapse(true);
		selection.removeAllRanges();
		selection.addRange(newRange);
	}

	/** Replace `<element>` with its children (unwrap). */
	_unwrapElement(element) {
		const parent = element.parentNode;
		while (element.firstChild) parent.insertBefore(element.firstChild, element);
		parent.removeChild(element);
	}

	/**
	 * If both range boundaries live inside one wrapper of `tagName`, return that wrapper.
	 * Optionally require `predicate(wrapper)` to be truthy (used for styled spans).
	 */
	_commonWrapperForRange(range, tagName, predicate) {
		const selector = this._selectorForMark(tagName);
		const startEl = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement;
		const endEl = range.endContainer.nodeType === 1 ? range.endContainer : range.endContainer.parentElement;

		const a = startEl ? startEl.closest(selector) : null;
		if (!a) return null;
		if (!a.contains(endEl)) return null;
		if (!this.root.contains(a)) return null;
		if (predicate && !predicate(a)) return null;

		return this._rangeWithinNode(range, a) ? a : null;
	}

	/** Is entire `range` inside `node` (inclusive bounds)? */
	_rangeWithinNode(range, node) {
		const nodeRange = document.createRange();
		nodeRange.selectNodeContents(node);
		const startsAfterOrEqual = nodeRange.compareBoundaryPoints(Range.START_TO_START, range) <= 0;
		const endsBeforeOrEqual = nodeRange.compareBoundaryPoints(Range.END_TO_END, range) >= 0;
		return startsAfterOrEqual && endsBeforeOrEqual;
	}

	/** Does `range` fully cover `node`? (inclusive) */
	_rangeCoversNode(range, node) {
		const nodeRange = document.createRange();
		nodeRange.selectNodeContents(node);
		const startsAtOrBefore = range.compareBoundaryPoints(Range.START_TO_START, nodeRange) <= 0;
		const endsAtOrAfter = range.compareBoundaryPoints(Range.END_TO_END, nodeRange) >= 0;
		return startsAtOrBefore && endsAtOrAfter;
	}

	/**
	 * Flatten nested tags of the same type (e.g., <em><em>…</em></em> → <em>…</em>).
	 * For spans, only flatten when `equalFn(inner, outer)` is true (same styles).
	 */
	_flattenNested(tagName, equalFn) {
		// Process deepest first to avoid churn on ancestors.
		const candidates = Array.from(this.root.querySelectorAll(tagName)).reverse();

		for (const inner of candidates) {
			if (!this.root.contains(inner)) continue;

			const outer = inner.parentElement && inner.parentElement.closest(tagName);
			if (!outer || !this.root.contains(outer)) continue;

			// For styled <span>, require style equality to flatten
			if (equalFn && !equalFn(inner, outer)) continue;

			const parent = inner.parentNode;
			if (!parent) continue;

			while (inner.firstChild) parent.insertBefore(inner.firstChild, inner);
			parent.removeChild(inner);
		}
	}

	/**
	 * Merge adjacent sibling tags of the same type.
	 * For spans, only merge when `equalFn(a, b)` is true (style equality).
	 */
	_mergeAdjacent(tagName, equalFn) {
		const walker = document.createTreeWalker(this.root, NodeFilter.SHOW_ELEMENT);
		const toMerge = [];

		while (walker.nextNode()) {
			const element = walker.currentNode;
			if (element.tagName?.toLowerCase() !== tagName.toLowerCase()) continue;

			const nextSibling = element.nextSibling;
			const nextIsSameTag = nextSibling?.nodeType === 1 && nextSibling.tagName.toLowerCase() === tagName.toLowerCase();

			if (nextIsSameTag) {
				if (!equalFn || equalFn(element, nextSibling)) {
					toMerge.push([element, nextSibling]);
				}
			}
		}

		toMerge.forEach(([a, b]) => {
			while (b.firstChild) a.appendChild(b.firstChild);
			b.remove();
		});
	}

	/** Housekeeping: remove empties, flatten nests, merge adjacents, normalize text nodes. */
	_cleanInline() {
		const inlineTags = ['strong', 'em', 'u', 'span'];

		// Remove empty wrappers first
		for (const tag of inlineTags) this._removeEmptyWrappers(tag);

		// Flatten nested tags
		for (const tag of inlineTags) {
			if (tag === 'span') {
				this._flattenNested('span', (a, b) => a.style?.cssText === b.style?.cssText);
			} else {
				this._flattenNested(tag);
			}
		}

		// Merge adjacent runs
		for (const tag of inlineTags) {
			if (tag === 'span') {
				this._mergeAdjacent('span', (a, b) => a.style?.cssText === b.style?.cssText);
			} else {
				this._mergeAdjacent(tag);
			}
		}

		this.root.normalize();
	}

	// =====================================================================
	// Toggle command (main entry for bold/italic/underline)
	// =====================================================================

	/**
	 * Toggle a mark over the current selection or at the caret.
	 * - Caret path: enter/exit mark at caret (delegates to _toggleMarkAtCaret)
	 * - Selection path:
	 *    * if whole selection is inside ONE wrapper → split that wrapper
	 *    * else remove across selection, or apply across selection
	 * - Runs light cleanup on selection path; skips heavy cleanup on caret path
	 */
	_toggleTag(tagName) {
		this._logSelection(`before toggle(${tagName})`);
		this._logHtml('HTML before');

		const sel = window.getSelection();
		if (!sel || sel.rangeCount === 0) return;
		const range = sel.getRangeAt(0);
		if (!this.root.contains(range.commonAncestorContainer)) return;

		// --- CARET (collapsed) ----------------------------------------------------
		if (range.collapsed) {
			// Prefer the newer caret handler if present; fall back to the older one
			if (typeof this._toggleMarkAtCaret === 'function') {
				this._toggleMarkAtCaret(tagName);
			} else {
				this._toggleAtCaret(tagName);
			}

			// Important: avoid aggressive cleaning here (we often rely on ZWSP anchors)
			this._logSelection(`after toggle(${tagName}) [caret]`);
			this._logHtml('HTML after [caret]');
			this._emit();
			return;
		}

		// --- SELECTION (non-collapsed) -------------------------------------------
		// Determine overall state of the mark within the selection
		let state = this._markStateInRange(tagName, range);

		// Small UX nicety: if selection text exactly matches a wrapper's text, snap to it
		if (state !== 'all' && typeof this._snapRangeToWrapperIfTextMatches === 'function') {
			const snap = this._snapRangeToWrapperIfTextMatches(range, tagName);
			if (snap) {
				// Replace range with the exact wrapper range for precise removal
				sel.removeAllRanges();
				sel.addRange(snap.range);
				state = 'all';
			}
		}

		// Fast path: if both endpoints live inside the SAME wrapper of this tag, split that run
		const sharedWrapper = this._commonWrapperForRange(sel.getRangeAt(0), tagName);

		const removing = state === 'all' || state === 'mixed';
		if (removing) {
			if (sharedWrapper) {
				// Remove mark only for the selected middle of THIS wrapper
				this._toggleOffInside(sharedWrapper, sel.getRangeAt(0));
			} else {
				// Selection spans multiple runs → remove everywhere inside selection
				this._unwrapTagAcrossRange(tagName, sel.getRangeAt(0));
			}
		} else {
			// Apply the mark across the selection
			this._wrapRangeWithTag(sel.getRangeAt(0), tagName);
		}

		// Now we can safely clean inline structure (empties/merges/normalize)
		// Note: keep caret cleanup light; selection cleanup is fine here.
		if (typeof this._cleanInline === 'function') {
			this._cleanInline();
		}

		this._logSelection(`after toggle(${tagName})`);
		this._logHtml('HTML after');
		this._emit();
	}
	/**
	 * Remove `wrapper` for the portion covered by `range`, preserving other marks.
	 * Splits the wrapper into left/middle/right using bookmarks INSIDE the wrapper.
	 */
	_toggleOffInside(wrapper, range) {
		const doc = this.root.ownerDocument || document;

		// 1) Surround selection with a neutral container
		const container = doc.createElement('span');
		container.setAttribute('data-unmark', wrapper.tagName.toLowerCase());

		const work = range.cloneRange();
		try {
			work.surroundContents(container);
		} catch {
			const frag = work.extractContents();
			container.appendChild(frag);
			work.insertNode(container);
		}

		// 2) Split THIS wrapper around the container (left stays wrapped, right stays wrapped)
		this._splitWrapperAroundNode(wrapper, container);

		// 3) Remove nested wrappers of the same type inside the container (rare, but safe)
		const tag = wrapper.tagName.toLowerCase();
		Array.from(container.querySelectorAll(tag)).forEach(el => this._unwrapElement(el));

		// 4) Replace container with its children (middle becomes plain)
		const parent = container.parentNode;
		const first = container.firstChild;
		const last = container.lastChild;
		while (container.firstChild) parent.insertBefore(container.firstChild, container);
		parent.removeChild(container);

		// 5) Restore selection on the middle
		const sel = window.getSelection();
		const nr = doc.createRange();
		if (first && last) {
			nr.setStartBefore(first);
			nr.setEndAfter(last);
		} else {
			nr.setStart(parent, Math.min(parent.childNodes.length, 0));
			nr.collapse(true);
		}
		sel.removeAllRanges();
		sel.addRange(nr);
	}

	/**
	 * Toggle mark at caret (no selection): if caret is inside a wrapper, split to exit it;
	 * otherwise insert an empty wrapper and place caret inside to start typing with the mark.
	 */
	// Robust toggle at caret: exit if inside mark, else enter.
	_toggleMarkAtCaret(tagName) {
		const sel = window.getSelection();
		if (!sel || sel.rangeCount === 0) return;
		const r = sel.getRangeAt(0);
		if (!this.root.contains(r.commonAncestorContainer) || !r.collapsed) return;

		const doc = this.root.ownerDocument || document;
		const anchorEl = r.startContainer.nodeType === 1 ? r.startContainer : r.startContainer.parentElement;
		const wrapper = this._closestMark(anchorEl, tagName);

		// --- EXIT mark: split wrapper and drop a plain text anchor between parts
		if (wrapper && this.root.contains(wrapper)) {
			// Put a temporary bookmark at the caret
			const bm = doc.createTextNode('');
			const caret = r.cloneRange();
			caret.insertNode(bm);

			// Extract left/right of the wrapper around the bookmark
			const leftRange = doc.createRange();
			leftRange.setStart(wrapper, 0);
			leftRange.setEndBefore(bm);
			const leftFrag = leftRange.extractContents();

			const rightRange = doc.createRange();
			rightRange.setStartAfter(bm);
			rightRange.setEnd(wrapper, wrapper.childNodes.length);
			const rightFrag = rightRange.extractContents();

			const parent = wrapper.parentNode;
			if (!parent) return;

			const leftWrap = wrapper.cloneNode(false);
			if (leftFrag.childNodes.length) leftWrap.appendChild(leftFrag);

			const rightWrap = wrapper.cloneNode(false);
			if (rightFrag.childNodes.length) rightWrap.appendChild(rightFrag);

			// Replace wrapper with [left?][ANCHOR text node][right?]
			const plainAnchor = doc.createTextNode('\u200B'); // ← plain typing host
			const frag = doc.createDocumentFragment();
			if (leftWrap.firstChild) frag.appendChild(leftWrap);
			frag.appendChild(plainAnchor);
			if (rightWrap.firstChild) frag.appendChild(rightWrap);
			parent.replaceChild(frag, wrapper);

			// Place caret inside the plain text anchor (after ZWSP)
			const nr = doc.createRange();
			nr.setStart(plainAnchor, 1);
			nr.collapse(true);
			sel.removeAllRanges();
			sel.addRange(nr);

			// Remove the temp bookmark if still around (usually consumed by ranges)
			bm.parentNode && bm.parentNode.removeChild(bm);

			// Light cleanup only (do NOT normalize/clean heavily here)
			this._removeEmptyWrappers('strong');
			this._removeEmptyWrappers('b');
			return;
		}

		// --- ENTER mark: create canonical wrapper with an inner ZWSP anchor
		const canonical = this._canonicalTag(tagName);
		const wrap = doc.createElement(canonical);
		const zwsp = doc.createTextNode('\u200B');
		wrap.appendChild(zwsp);
		r.insertNode(wrap);

		// Caret inside the new wrapper, after ZWSP
		const nr = doc.createRange();
		nr.setStart(zwsp, 1);
		nr.collapse(true);
		sel.removeAllRanges();
		sel.addRange(nr);

		// No heavy cleanup here—keep the ZWSP anchor until the user types.
	}

	/**
	 * Remove `tagName` everywhere INSIDE the selection, even if the selection
	 * originally lived inside ancestors that extend beyond it.
	 * (Container-lift strategy.)
	 */
	_unwrapTagAcrossRange(tagName, range) {
		const doc = this.root.ownerDocument || document;

		// 1) Surround selection with a neutral container
		const container = doc.createElement('span');
		container.setAttribute('data-unmark', tagName);

		const work = range.cloneRange();
		try {
			work.surroundContents(container);
		} catch {
			const fragment = work.extractContents();
			container.appendChild(fragment);
			work.insertNode(container);
		}

		// 2) Repeatedly lift the container out of any ancestors of this tag
		let host = container.parentElement ? container.parentElement.closest(tagName) : null;
		while (host && this.root.contains(host)) {
			this._splitWrapperAroundNode(host, container);
			host = container.parentElement ? container.parentElement.closest(tagName) : null;
		}

		// 3) Remove nested <tagName> inside the container (if any)
		Array.from(container.querySelectorAll(tagName)).forEach(el => this._unwrapElement(el));

		// 4) Restore selection to plain content and dissolve container
		const selection = window.getSelection();
		const newRange = doc.createRange();
		const first = container.firstChild;
		const last = container.lastChild;

		if (first && last) {
			newRange.setStartBefore(first);
			newRange.setEndAfter(last);
		} else {
			newRange.selectNode(container);
			newRange.collapse(true);
		}
		selection.removeAllRanges();
		selection.addRange(newRange);

		const parent = container.parentNode;
		if (parent) {
			while (container.firstChild) parent.insertBefore(container.firstChild, container);
			parent.removeChild(container);
		}
	}

	/*_unwrapWholeWrapper(wrapper) {
		const doc = this.root.ownerDocument || document;
		const parent = wrapper.parentNode;
		if (!parent) return;

		const first = wrapper.firstChild;
		const last = wrapper.lastChild;

		// Move children out, then remove the wrapper
		while (wrapper.firstChild) parent.insertBefore(wrapper.firstChild, wrapper);
		parent.removeChild(wrapper);

		// Restore selection over what used to be the wrapper contents
		const sel = window.getSelection();
		const nr = doc.createRange();
		if (first && last) {
			nr.setStartBefore(first);
			nr.setEndAfter(last);
		} else {
			nr.setStart(parent, Math.min(parent.childNodes.length, 0));
			nr.collapse(true);
		}
		sel.removeAllRanges();
		sel.addRange(nr);
	}*/

	_unwrapWholeWrapper(wrapper) {
		this._unwrapElement(wrapper);
	}

	/** Remove all occurrences of `tagName` inside a DocumentFragment (helper). */
	_stripTagInFragment(fragment, tagName) {
		Array.from(fragment.querySelectorAll(tagName)).forEach(el => {
			const parent = el.parentNode;
			while (el.firstChild) parent.insertBefore(el.firstChild, el);
			parent.removeChild(el);
		});
		return fragment;
	}

	/**
	 * Split a wrapper so that `node` becomes a sibling between left and right parts.
	 * Used by both middle-removal and container-lift steps.
	 */
	_splitWrapperAroundNode(wrapper, node) {
		const doc = this.root.ownerDocument || document;
		if (!wrapper || !node || !wrapper.contains(node)) return null;
		const parent = wrapper.parentNode;
		if (!parent) return null;

		const leftRange = doc.createRange();
		leftRange.setStart(wrapper, 0);
		leftRange.setEndBefore(node);
		const leftFrag = leftRange.extractContents();

		const rightRange = doc.createRange();
		rightRange.setStartAfter(node);
		rightRange.setEnd(wrapper, wrapper.childNodes.length);
		const rightFrag = rightRange.extractContents();

		const leftWrap = wrapper.cloneNode(false);
		if (leftFrag.childNodes.length) leftWrap.appendChild(leftFrag);

		const rightWrap = wrapper.cloneNode(false);
		if (rightFrag.childNodes.length) rightWrap.appendChild(rightFrag);

		const frag = doc.createDocumentFragment();
		if (leftWrap.firstChild) frag.appendChild(leftWrap);
		frag.appendChild(node);
		if (rightWrap.firstChild) frag.appendChild(rightWrap);

		parent.replaceChild(frag, wrapper);

		return {
			left: leftWrap.firstChild ? leftWrap : null,
			right: rightWrap.firstChild ? rightWrap : null,
			parent,
		};
	}

	/** Ensure the root has focus before mutating selection. */
	_ensureFocus() {
		if (document.activeElement !== this.root) {
			this.root.focus({ preventScroll: true });
		}
	}

	// =====================================================================
	// Cleanup helpers
	// =====================================================================

	/** Remove empty wrappers of a given tag (recursively empty is still empty). */
	_removeEmptyWrappers(tagName) {
		const sel = window.getSelection();
		const caretNode = sel && sel.rangeCount ? sel.anchorNode : null;

		const els = Array.from(this.root.querySelectorAll(tagName));
		els.forEach(el => {
			if (!this.root.contains(el)) return;

			const caretInside = !!(caretNode && this.root.contains(caretNode) && el.contains(caretNode) && sel && sel.isCollapsed);

			// consider only non-breaking content as "text"
			const text = (el.textContent || '').replace(/\u200B/g, '');
			const hasText = this._normalizeText(text).length > 0;

			const hasNonBrElement = Array.from(el.childNodes).some(n => n.nodeType === 1 && n.nodeName !== 'BR');

			if (!hasText && !hasNonBrElement && !caretInside) {
				this._unwrapElement(el);
			}
		});
	}

	/** Normalize whitespace for comparisons (collapses spaces and NBSPs). */
	// Treat ZWSP/NBSP as whitespace in all emptiness checks.
	_normalizeText(s) {
		return (s || '')
			.replace(/\u200B/g, '') // ZWSP
			.replace(/\uFEFF/g, '') // ZWNBSP/BOM
			.replace(/\u00A0/g, ' ') // NBSP→space
			.replace(/\s+/g, ' ')
			.trim();
	}

	/**
	 * If selection text equals exactly one wrapper's text, snap the selection to that wrapper.
	 * Helps avoid off-by-one micro-nests when users roughly select the run.
	 */
	_snapRangeToWrapperIfTextMatches(range, tagName) {
		const candidates = new Set();

		const maybeAdd = el => {
			if (el && el.nodeType === 1 && this.root.contains(el) && el.matches(tagName)) {
				candidates.add(el);
			}
		};

		const startElement = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement;
		const endElement = range.endContainer.nodeType === 1 ? range.endContainer : range.endContainer.parentElement;

		maybeAdd(startElement && startElement.closest(tagName));
		maybeAdd(endElement && endElement.closest(tagName));

		// Also consider immediate neighbors at text boundaries
		const sc = range.startContainer;
		if (sc && sc.nodeType === Node.TEXT_NODE) {
			const txt = sc.textContent || '';
			if (range.startOffset === txt.length) maybeAdd(sc.nextSibling);
			if (range.startOffset === 0) maybeAdd(sc.previousSibling);
		}
		const ec = range.endContainer;
		if (ec && ec.nodeType === Node.TEXT_NODE) {
			const txt = ec.textContent || '';
			if (range.endOffset === 0) maybeAdd(ec.previousSibling);
			if (range.endOffset === txt.length) maybeAdd(ec.nextSibling);
		}

		const selectedText = this._normalizeText(range.toString());
		if (!selectedText) return null;

		for (const wrapper of candidates) {
			const wrapperText = this._normalizeText(wrapper.textContent);
			if (wrapperText && wrapperText === selectedText) {
				const snapped = document.createRange();
				snapped.selectNodeContents(wrapper);
				return { wrapper, range: snapped };
			}
		}
		return null;
	}

	/** Copy attributes from one element to another (class, style, etc.). */
	_copyAttrs(from, to) {
		for (let i = 0; i < from.attributes.length; i++) {
			const attr = from.attributes[i];
			to.setAttribute(attr.name, attr.value);
		}
	}

	/** Does `range` intersect this DOM node? (Used by the text walker.) */
	_rangeIntersectsNode(range, node) {
		const nodeRange = document.createRange();
		try {
			node.nodeType === Node.TEXT_NODE ? nodeRange.selectNodeContents(node) : nodeRange.selectNode(node);
		} catch {
			return false;
		}
		// range.start < nodeRange.end && range.end > nodeRange.start
		return range.compareBoundaryPoints(Range.START_TO_END, nodeRange) < 0 && range.compareBoundaryPoints(Range.END_TO_START, nodeRange) > 0;
	}

	/**
	 * Return 'all' | 'none' | 'mixed' for a given mark inside a selection range.
	 * - Counts only text nodes that INTERSECT the range and contain non-whitespace content
	 * - Treats <strong>/<b> and <em>/<i> as equivalent
	 */
	_markStateInRange(tagName, range) {
		// Map mark names to a selector that covers HTML aliases
		const selector =
			tagName === 'strong' || tagName === 'b'
				? 'strong,b'
				: tagName === 'em' || tagName === 'i'
				? 'em,i'
				: tagName === 'u'
				? 'u'
				: tagName === 'span'
				? 'span'
				: tagName;

		const root = this.root;
		const common = range.commonAncestorContainer.nodeType === 1 ? range.commonAncestorContainer : range.commonAncestorContainer.parentNode;

		let totalTextNodes = 0;
		let markedTextNodes = 0;

		const walker = document.createTreeWalker(common, NodeFilter.SHOW_TEXT, {
			acceptNode: node => {
				if (!root.contains(node)) return NodeFilter.FILTER_REJECT;
				if (!this._rangeIntersectsNode(range, node)) return NodeFilter.FILTER_REJECT;

				// Normalize text: ignore pure whitespace / nbsp / zwsp
				const t = this._normalizeText(node.nodeValue || '');
				return t ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
			},
		});

		while (walker.nextNode()) {
			totalTextNodes++;
			const textNode = walker.currentNode;
			const hostEl = textNode.parentElement;
			// "Is this textNode inside the mark?"
			const wrapper = hostEl && hostEl.closest(selector);
			if (wrapper && root.contains(wrapper)) {
				markedTextNodes++;
			}
		}

		if (totalTextNodes === 0) return 'none';
		if (markedTextNodes === 0) return 'none';
		if (markedTextNodes === totalTextNodes) return 'all';
		return 'mixed';
	}

	// =====================================================================
	// Debug helpers
	// =====================================================================

	_d(...args) {
		if (!this.debug) return;
		console.log('[EditorEngine]', ...args);
	}

	_nodeInfo(node) {
		if (!node) return '<null>';
		if (node.nodeType === Node.TEXT_NODE) {
			const text = node.textContent ?? '';
			return `#text("${text.length > 30 ? text.slice(0, 27) + '…' : text}")`;
		}
		return `<${node.nodeName.toLowerCase()}>`;
	}

	_pathToRoot(node) {
		const path = [];
		let current = node && (node.nodeType === 1 ? node : node.parentElement);
		while (current && current !== this.root && current.nodeType === 1) {
			let index = 0;
			let sibling = current;
			while ((sibling = sibling.previousSibling)) {
				if (sibling.nodeType === 1 && sibling.nodeName === current.nodeName) index++;
			}
			path.push(`${current.nodeName.toLowerCase()}[${index}]`);
			current = current.parentNode;
		}
		path.push('ROOT');
		return path.reverse().join(' > ');
	}

	_logSelection(label = 'sel') {
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) return this._d(`${label}: (no selection)`);

		const range = selection.getRangeAt(0);
		const inside = this.root.contains(range.commonAncestorContainer);
		const text = range.toString();

		this._d(`${label}: inside=${inside} collapsed=${range.collapsed} text="${text}"`);
		this._d(`  start: ${this._nodeInfo(range.startContainer)} @${range.startOffset}  path: ${this._pathToRoot(range.startContainer)}`);
		this._d(`  end:   ${this._nodeInfo(range.endContainer)}   @${range.endOffset}    path: ${this._pathToRoot(range.endContainer)}`);
		if (inside) this._logWrappersAroundPoint(range.startContainer);
	}

	_logWrappersAroundPoint(node) {
		const element = node.nodeType === 1 ? node : node.parentElement;
		if (!element) return;

		const strong = element.closest('strong');
		const em = element.closest('em');
		const u = element.closest('u');
		const spanFs = element.closest('span');
		const fontSize = spanFs && spanFs.style && spanFs.style.fontSize ? spanFs.style.fontSize : null;

		this._d('  wrappers: ', {
			strong: !!(strong && this.root.contains(strong)),
			em: !!(em && this.root.contains(em)),
			u: !!(u && this.root.contains(u)),
			fontSize,
		});
	}

	/** Which wrappers overlap the range? Debug helper. */
	_logWrappersInRange(tagName, range) {
		const all = Array.from(this.root.querySelectorAll(tagName));
		const touching = all.filter(el => {
			const startIn = el.contains(range.startContainer);
			const endIn = el.contains(range.endContainer);
			return startIn || endIn;
		});
		this._d(
			`wrappers <${tagName}> touching range:`,
			touching.map(el => this._pathToRoot(el)),
		);
	}

	_logHtml(label = 'HTML') {
		const html = this.root.innerHTML
			.replace(/\s{2,}/g, ' ')
			.replace(/</g, '\n<') // line break before tags for readability
			.trim();
		this._d(`${label}:\n${html}`);
	}
}
